// context/UserContext.jsx
'use client'
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useSession, signIn, signOut } from 'next-auth/react'
import LoginModal from '@components/Auth/LoginModal'

const UserContext = createContext(null)

export function UserProvider({ children }) {
  const { data: session, status, update } = useSession()
  const user = session?.user ?? null

  // ─── Login modal (Google + magic link) ──────────────────────────────────────
  const [loginModal, setLoginModal] = useState({ open: false, lang: 'en', callbackUrl: undefined })

  // openLogin({ lang, callbackUrl }) — call from anywhere (navbar, "save property" button…)
  const openLogin  = useCallback(({ lang, callbackUrl } = {}) =>
    setLoginModal({ open: true, lang: lang ?? 'en', callbackUrl }), [])
  const closeLogin = useCallback(() => setLoginModal(s => ({ ...s, open: false })), [])

  useEffect(() => { if (status === 'authenticated') closeLogin() }, [status, closeLogin])

  // Skip the modal and go straight to a provider, e.g. login('google').
  // Return to the page the user was on — every page is public, so that's always safe.
  const login = useCallback((provider = 'google', { callbackUrl } = {}) =>
    signIn(provider, { callbackUrl: callbackUrl ?? window.location.href }), [])

  const logout = useCallback(({ callbackUrl } = {}) =>
    signOut({ callbackUrl: callbackUrl ?? window.location.href }), [])

  // update() needs an argument to hit the server with trigger === 'update'
  const refreshUser = useCallback(() => update({ refresh: Date.now() }), [update])

  const updateUser = useCallback(async (patch) => {
    const res = await fetch('/api/user', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(data?.error ?? 'Could not update your profile')
    await refreshUser()
    return data.user
  }, [refreshUser])

  const value = useMemo(() => ({
    user,
    status,                                          // 'loading' | 'authenticated' | 'unauthenticated'
    isLoading:  status === 'loading',
    isLoggedIn: status === 'authenticated' && !!user,
    openLogin,
    closeLogin,
    login,
    logout,
    refreshUser,
    updateUser,
  }), [user, status, openLogin, closeLogin, login, logout, refreshUser, updateUser])

  return (
    <UserContext.Provider value={value}>
      {children}
      <LoginModal open={loginModal.open} lang={loginModal.lang} callbackUrl={loginModal.callbackUrl} onClose={closeLogin} />
    </UserContext.Provider>
  )
}

export function useUser() {
  const ctx = useContext(UserContext)
  if (!ctx) throw new Error('useUser must be used inside <UserProvider>')
  return ctx
}